import Chip from "@mui/material/Chip";
import Collapse from "@mui/material/Collapse";
import Grid from "@mui/material/Grid";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useQuery } from "@tanstack/react-query";
import { ExternalLink, Mail, Phone } from "lucide-mui";
import { useState } from "react";
import {
  api,
  type ContactChannelHistoryItem,
  type ContactReachability,
} from "../../../api/client.ts";
import { formatDate } from "../../../utils/format.ts";
import { keys } from "../../../api/queryKeys.ts";
import DetailRow from "../../../components/DetailRow.tsx";
import EmptyState from "../../../components/EmptyState.tsx";
import SectionCard from "../../../components/SectionCard.tsx";

interface ReachabilityCardProps {
  reachability: ContactReachability;
  contactId: string;
}

const DEPRECATION_LABEL: Record<string, string> = {
  employer_changed: "former employer",
  bounced: "bounced",
  absent_from_source: "gone from source",
  superseded: "superseded",
  manual: "retired manually",
};

function channelIcon(type: string) {
  if (type === "phone") return <Phone fontSize="small" />;
  if (type === "linkedin") return <ExternalLink fontSize="small" />;
  return <Mail fontSize="small" />;
}

function PreviousValue({ channel }: { channel: ContactChannelHistoryItem }) {
  const lastSeen = formatDate(channel.last_seen_at, "month", "") || null;
  const reason = channel.deprecated_reason
    ? (DEPRECATION_LABEL[channel.deprecated_reason] ?? channel.deprecated_reason)
    : null;

  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
      {channelIcon(channel.channel_type)}
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        {channel.value}
      </Typography>
      {reason && (
        <Tooltip
          title={
            channel.master_company_name
              ? `Tied to ${channel.master_company_name}, where this person no longer has an open role`
              : "No longer the best way to reach this person"
          }
        >
          <Chip label={reason} size="small" variant="outlined" />
        </Tooltip>
      )}
      {lastSeen && (
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          last seen {lastSeen}
        </Typography>
      )}
    </Stack>
  );
}

export default function ReachabilityCard({ reachability, contactId }: ReachabilityCardProps) {
  const { email, phone, linkedin_url } = reachability;
  const hasAny = email || phone || linkedin_url;
  const [showPrevious, setShowPrevious] = useState(false);

  const { data: channels } = useQuery({
    queryKey: keys.masterContact.channels(contactId),
    queryFn: () => api.getContactChannels(contactId),
    enabled: Boolean(contactId),
  });

  // Contact info is never deleted, only demoted — so anything not currently
  // best is still here, with the reason it was set aside.
  const best = new Set([email, phone, linkedin_url].filter(Boolean) as string[]);
  const previous = (channels ?? []).filter((c) => !c.is_active || !best.has(c.value));

  return (
    <SectionCard title="Reachability">
      {!hasAny && <EmptyState title="No contact channels on record yet" />}
      {hasAny && (
        <Grid container columnSpacing={3}>
          <Grid size={{ xs: 12, md: 4 }}>
            <DetailRow label="Email" value={email && <Link href={`mailto:${email}`}>{email}</Link>} />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <DetailRow label="Phone" value={phone && <Link href={`tel:${phone}`}>{phone}</Link>} />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <DetailRow
              label="LinkedIn"
              value={
                linkedin_url && (
                  <Link href={`https://${linkedin_url}`} target="_blank" rel="noopener noreferrer">
                    {linkedin_url}
                  </Link>
                )
              }
            />
          </Grid>
        </Grid>
      )}

      {previous.length > 0 && (
        <>
          <Link
            component="button"
            variant="caption"
            onClick={() => setShowPrevious((open) => !open)}
            sx={{ mt: 0.5, display: "block" }}
          >
            {showPrevious ? "Hide" : "Show"} previous contact info ({previous.length})
          </Link>
          <Collapse in={showPrevious}>
            <Stack spacing={0.75} sx={{ mt: 1 }}>
              {previous.map((channel) => (
                <PreviousValue key={channel.id} channel={channel} />
              ))}
            </Stack>
          </Collapse>
        </>
      )}
    </SectionCard>
  );
}
